import { lazy, Suspense, useEffect, useMemo, useState } from "react"

import {
  Box,
  Typography,
  Card,
  CardContent,
  Skeleton,
  Alert,
} from '@mui/material'

import {
  handleApiError,
  usersService,
  ordersService,
  packageMappingsService,
  type UserAnalytics,
  type OrderAnalytics,
  type PackageMappingsResponse,
} from "../../api"

import { toCumulativeSignups } from './analytics-utils'
import { ConversionRateCards } from './ConversionRateCards'
import { FunnelDiagram } from './FunnelDiagram'
import { StatCard, BreakdownSection } from './StatCard'

const SignupTrendChart = lazy(() => import('./SignupTrendChart'))
const OrderStatusPipeline = lazy(() => import('./OrderStatusPipeline'))
const RevenueSummary = lazy(() => import('./RevenueSummary'))
const PackagePopularity = lazy(() => import('./PackagePopularity'))
const GeographyBreakdown = lazy(() => import('./GeographyBreakdown'))

const EMPTY_STATS = {
  total: 0,
  verifiedPurchased: 0,
  verifiedNotPurchased: 0,
  unverifiedPurchased: 0,
  unverifiedNotPurchased: 0,
} as const

const buildPackageMap = (response: PackageMappingsResponse): Record<string, { name: string }> => {
  const source = response.environment === 'production' ? response.prod : response.dev
  const map: Record<string, { name: string }> = {}
  for (const [id, pkg] of Object.entries(source)) {
    map[id] = { name: pkg.name }
  }
  return map
}

const limitToTopCountries = (countries: ReadonlyArray<{ country: string; count: number }>) => {
  if (countries.length <= 10) return [...countries]
  const top10 = countries.slice(0, 10)
  const otherCount = countries.slice(10).reduce((sum, e) => sum + e.count, 0)
  return [...top10, { country: 'Other', count: otherCount }]
}

function ChartSkeletons() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {[1, 2, 3].map(i => (
        <Skeleton key={i} variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
      ))}
    </Box>
  )
}

function OrdersSkeleton() {
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
      {[1, 2, 3].map(i => (
        <Skeleton key={i} variant="rectangular" height={300} sx={{ borderRadius: 1 }} />
      ))}
    </Box>
  )
}

export default function AnalyticsPage() {
  const [userAnalytics, setUserAnalytics] = useState<UserAnalytics | null>(null)
  const [analytics, setAnalytics] = useState<OrderAnalytics | null>(null)
  const [packageMap, setPackageMap] = useState<Record<string, { name: string }>>({})
  const [isUsersLoading, setIsUsersLoading] = useState(true)
  const [isOrdersLoading, setIsOrdersLoading] = useState(true)
  const [usersError, setUsersError] = useState("")
  const [ordersError, setOrdersError] = useState("")

  useEffect(() => {
    const fetchUserAnalytics = async () => {
      try {
        setIsUsersLoading(true)
        setUsersError("")
        const data = await usersService.getUserAnalytics()
        setUserAnalytics(data)
      } catch (err) {
        setUsersError(handleApiError(err))
      } finally {
        setIsUsersLoading(false)
      }
    }

    fetchUserAnalytics()
  }, [])

  useEffect(() => {
    const fetchOrderAnalytics = async () => {
      try {
        setIsOrdersLoading(true)
        setOrdersError("")
        const [orderAnalytics, pkgMappings] = await Promise.all([
          ordersService.getOrderAnalytics(),
          packageMappingsService.getPackageMappings(),
        ])
        setAnalytics(orderAnalytics)
        setPackageMap(buildPackageMap(pkgMappings))
      } catch (err) {
        setOrdersError(handleApiError(err))
      } finally {
        setIsOrdersLoading(false)
      }
    }

    fetchOrderAnalytics()
  }, [])

  const overallStats = userAnalytics?.stats.overall ?? EMPTY_STATS
  const regularStats = userAnalytics?.stats.regular ?? EMPTY_STATS
  const contentOnlyStats = userAnalytics?.stats.contentOnly ?? EMPTY_STATS

  const signupTrendData = useMemo(
    () => userAnalytics ? toCumulativeSignups(userAnalytics.dailySignups) : [],
    [userAnalytics],
  )
  const geographyData = useMemo(
    () => userAnalytics ? limitToTopCountries(userAnalytics.countryBreakdown) : [],
    [userAnalytics],
  )
  const orderStatusData = useMemo(() => {
    if (!analytics) return []
    const statusOrder = ['purchased', 'created', 'received', 'in_progress', 'completed', 'failed', 'cancelled', 'unknown', 'deleted']
    return [...analytics.statusCounts].sort((a, b) => {
      const ai = statusOrder.indexOf(a.status)
      const bi = statusOrder.indexOf(b.status)
      return (ai === -1 ? 999 : ai) - (bi === -1 ? 999 : bi)
    })
  }, [analytics])
  const monthlyRevenueData = useMemo(() => analytics?.monthlyRevenue ?? [], [analytics])
  const packageData = useMemo(() => {
    if (!analytics) return []
    return analytics.packageCounts
      .map(({ packageId, count }) => ({
        name: (packageId && packageMap[packageId]?.name) ?? packageId ?? 'unknown',
        count,
      }))
      .sort((a, b) => b.count - a.count)
  }, [analytics, packageMap])

  if (isUsersLoading) {
    return (
      <Box sx={{ flex: 1, p: 3 }}>
        <Box sx={{ mb: 3 }}>
          <Skeleton variant="text" width={200} height={40} />
        </Box>
        <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
          {[1, 2, 3, 4, 5].map(i => (
            <Box key={i} sx={{ flex: '1 1 0', minWidth: 150 }}>
              <Skeleton variant="rectangular" height={90} sx={{ borderRadius: 1 }} />
            </Box>
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          {[1, 2].map(i => (
            <Box key={i} sx={{ flex: '1 1 0', minWidth: 300 }}>
              <Skeleton variant="rectangular" height={140} sx={{ borderRadius: 1 }} />
            </Box>
          ))}
        </Box>
      </Box>
    )
  }

  return (
    <Box sx={{ flex: 1, p: 3 }}>
      <Typography variant="h4" component="h1" fontWeight="bold" sx={{ mb: 3 }}>
        Analytics
      </Typography>

      {usersError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {usersError}
        </Alert>
      )}

      {ordersError && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {ordersError}
        </Alert>
      )}

      <Card variant="outlined" sx={{ mb: 3, bgcolor: 'grey.50' }}>
        <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
          <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
            How to read these metrics
          </Typography>
          <Typography variant="body2" color="text.secondary" component="div">
            <Box component="ul" sx={{ m: 0, pl: 2.5, '& li': { mb: 0.5 } }}>
              <li><strong>Verified</strong> — user completed email verification (entered the code). This is a proxy for "has logged in."</li>
              <li><strong>Unverified</strong> — account exists but the user never completed email verification.</li>
              <li><strong>Purchased</strong> — user has at least one order (ordersCount {'>'} 0).</li>
              <li><strong>Regular</strong> — users who signed up through the main app. They can order labs and access all features. An unverified regular user started signup in the app but never completed email verification.</li>
              <li><strong>Content-Only</strong> — users explicitly created as content-only accounts (e.g. via an invite or import). They only have access to content, no lab ordering. An unverified content-only user was provisioned but never opened/verified the account.</li>
            </Box>
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Each user falls into exactly one of the four segments (Verified/Unverified x Purchased/No Purchase). The segments add up to the total. Note that "unverified" means different things per user type: for regular users it means they dropped off during signup; for content-only users it means the account was created for them but they never activated it.
          </Typography>
        </CardContent>
      </Card>

      <Box sx={{ display: 'flex', gap: 2, mb: 3, flexWrap: 'wrap' }}>
        <StatCard label="Total Users" value={overallStats.total} description="All regular + content-only users" />
        <StatCard label="Verified + Purchased" value={overallStats.verifiedPurchased} color="success.main" description="Logged in and placed at least one order" />
        <StatCard label="Verified + No Purchase" value={overallStats.verifiedNotPurchased} color="success.light" description="Logged in but never placed an order" />
        <StatCard label="Unverified + Purchased" value={overallStats.unverifiedPurchased} color="warning.main" description="Never completed email verification but has an order (e.g. purchased before verifying)" />
        <StatCard label="Unverified + No Purchase" value={overallStats.unverifiedNotPurchased} color="text.secondary" description="Account created but never verified and never purchased" />
      </Box>

      <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
        <BreakdownSection title="Regular Users" stats={regularStats} />
        <BreakdownSection title="Content-Only Users" stats={contentOnlyStats} />
      </Box>

      <Typography variant="h5" fontWeight="bold" sx={{ mt: 4, mb: 2 }}>
        User Funnel
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
        <Card variant="outlined" sx={{ p: 3 }}>
          <FunnelDiagram title="All Users" stats={overallStats} />
        </Card>
        <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
          <Card variant="outlined" sx={{ p: 3, flex: '1 1 0', minWidth: 400 }}>
            <FunnelDiagram title="Regular Users" stats={regularStats} />
          </Card>
          <Card variant="outlined" sx={{ p: 3, flex: '1 1 0', minWidth: 400 }}>
            <FunnelDiagram
              title="Content-Only Users"
              stats={contentOnlyStats}
              unverifiedNoPurchaseNote="Always 0 — content-only users are created via purchase, so they always have an order. Any unverified user with no purchase is counted as regular."
            />
          </Card>
        </Box>
      </Box>

      <Box sx={{ mt: 4, display: 'flex', flexDirection: 'column', gap: 4 }}>
        <ConversionRateCards
          overallStats={overallStats}
          regularStats={regularStats}
          contentOnlyStats={contentOnlyStats}
        />

        <Suspense fallback={<ChartSkeletons />}>
          <SignupTrendChart data={signupTrendData} />

          {isOrdersLoading ? (
            <OrdersSkeleton />
          ) : (
            <>
              <OrderStatusPipeline data={orderStatusData} />
              <RevenueSummary
                totalRevenue={analytics?.totalRevenue ?? 0}
                paidOrdersCount={analytics?.paidOrdersCount ?? 0}
                monthlyRevenue={monthlyRevenueData}
              />
              <PackagePopularity data={packageData} />
            </>
          )}

          <GeographyBreakdown data={geographyData} />
        </Suspense>
      </Box>
    </Box>
  )
}
